import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ListChecks,
  Search,
  Filter,
  FileText,
  TrendingUp,
  Shield,
  CheckCircle,
  Zap,
  Globe,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function AiResultsAnalysis() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="AI Results Analysis"
        description="AI analysis of EU tender results: summaries, prioritization, and actionable insights. Extract key information from TED notices faster with TED Monitor."
        path="/ai-results-analysis"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/ai-results-analysis" },
          { lang: "it", href: "https://tedmonitor.eu/it/analisi-risultati-ai" },
          { lang: "x-default", href: "https://tedmonitor.eu/ai-results-analysis" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" />
              AI-powered tender insights
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              AI analysis of tender results for faster decisions
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              When monitoring EU public procurement, the bottleneck is not finding tenders—it is reading, interpreting,
              and prioritizing results at scale. TED Monitor&apos;s AI results analysis helps you extract the essential
              information, summarize notices, and focus on the opportunities that match your criteria.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/eu-tender-monitoring"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                EU tender monitoring
              </Link>
              <Link
                to="/ted-procurement-alerts"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Procurement alerts
              </Link>
              <Link
                to="/european-public-procurement-software"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Procurement software
              </Link>
              <Link
                to="/multilingual-access"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Multilingual access
              </Link>
              <Link
                to="/compliance-and-security"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Compliance &amp; security
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              Official source:{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                TED (Tenders Electronic Daily)
              </a>{" "}
              – Supplement to the Official Journal of the European Union.
            </p>
          </div>
        </section>

        {/* What it does */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <ListChecks className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">What AI results analysis does</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              The AI layer supports post-search workflow: it helps you understand the results faster and identify the
              most relevant tenders without manually reviewing every notice.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Summarize tender notices into clear, actionable highlights",
                "Extract key elements (scope, buyer, deadlines, locations, procedure hints, requirements)",
                "Highlight signals of fit vs. mismatch with your monitoring intent",
                "Support prioritization when you have many results",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              This is designed to reduce reading time and accelerate &quot;go / no-go&quot; decisions for tender
              participation.
            </p>
          </div>
        </section>

        {/* How it fits into workflow */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Designed for real monitoring workflows
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              AI analysis is most effective when paired with structured search and monitoring. A typical workflow:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { icon: Search, title: "Search", desc: "Run a structured query on TED data" },
                { icon: Filter, title: "Filter", desc: "Refine by CPV, countries, keywords" },
                { icon: FileText, title: "Analyze", desc: "AI summarizes and extracts key points" },
                { icon: TrendingUp, title: "Prioritize", desc: "Focus on the best opportunities first" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 bg-background rounded-lg border text-center">
                  <div className="mb-2 flex justify-center">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-medium text-foreground text-sm">{title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/eu-tender-monitoring" className="text-primary hover:underline font-medium">
                Explore EU tender monitoring →
              </Link>
              <Link to="/ted-procurement-alerts" className="text-primary hover:underline font-medium">
                Set up procurement alerts →
              </Link>
            </div>
          </div>
        </section>

        {/* Trust / Compliance */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Compliance-oriented by design</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              AI analysis operates within a platform designed for professional usage requirements, with secure access
              and privacy-conscious handling of account data.
            </p>

            <div className="flex flex-wrap gap-4 mb-4">
              {[
                { icon: Shield, label: "Secure authentication" },
                { icon: Globe, label: "EU-wide scope" },
                { icon: Lock, label: "Encrypted access" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </span>
              ))}
            </div>

            <p className="mt-4">
              <Link to="/compliance-and-security" className="text-primary hover:underline font-medium text-sm">
                Read about compliance &amp; security →
              </Link>
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Turn tender results into prioritized opportunities
            </h2>
            <p className="text-base mb-6 opacity-90">
              Start using AI analysis to reduce reading time and focus on the tenders that matter.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/eu-tender-monitoring" className="text-muted-foreground hover:text-foreground">
                EU Tender Monitoring
              </Link>
              <Link to="/ted-procurement-alerts" className="text-muted-foreground hover:text-foreground">
                Procurement Alerts
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Data Source
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
